import React, { useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { isWechatBrowser } from '@/utils/wechatJssdk';
import apiClient from '@/api/client';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { captureInviteFromUrl } from '@/utils/referralCapture';
import { analyticsApi } from '@/api/analytics';
import MainLayout from '@/layouts/MainLayout';
import AuthLayout from '@/layouts/AuthLayout';
import Login from '@/pages/Login';
import TenantSelect from '@/pages/TenantSelect';

/* 懒加载业务页面（带 chunk 加载失败自动重试） */
const Dashboard = lazyWithRetry(() => import('@/pages/Dashboard'));
const StructurePage = lazyWithRetry(() => import('@/pages/amoeba/StructurePage'));
const DepartmentPage = lazyWithRetry(() => import('@/pages/amoeba/DepartmentPage'));
const PositionPage = lazyWithRetry(() => import('@/pages/amoeba/PositionPage'));
const MemberPage = lazyWithRetry(() => import('@/pages/amoeba/MemberPage'));
const PersonnelConfigPage = lazyWithRetry(() => import('@/pages/amoeba/PersonnelConfigPage'));
const OrgChartPage = lazyWithRetry(() => import('@/pages/amoeba/OrgChartPage'));
const AccountDefinePage = lazyWithRetry(() => import('@/pages/account/AccountDefinePage'));
const AccountConfigPage = lazyWithRetry(() => import('@/pages/account/AccountConfigPage'));
const DailyEntryPage = lazyWithRetry(() => import('@/pages/entry/DailyEntryPage'));
const SalesTransactionPage = lazyWithRetry(() => import('@/pages/sales/SalesTransactionPage'));
const MonthlyReportPage = lazyWithRetry(() => import('@/pages/report/MonthlyReportPage'));
const CompanyRollupPage = lazyWithRetry(() => import('@/pages/report/CompanyRollupPage'));
const ReportCreatePage = lazyWithRetry(() => import('@/pages/report/ReportCreatePage'));
const AnnualReportPage = lazyWithRetry(() => import('@/pages/report/AnnualReportPage'));
const TransactionListPage = lazyWithRetry(() => import('@/pages/account/TransactionListPage'));
const TransactionInitiatePage = lazyWithRetry(() => import('@/pages/account/TransactionInitiatePage'));
const TemplateMarketPage = lazyWithRetry(() => import('@/pages/account/TemplateMarketPage'));
const QuotationMarketPage = lazyWithRetry(() => import('@/pages/account/QuotationMarketPage'));
const DataImportPage = lazyWithRetry(() => import('@/pages/account/DataImportPage'));
const AllocationExecutePage = lazyWithRetry(() => import('@/pages/account/AllocationExecutePage'));
const BomImportPage = lazyWithRetry(() => import('@/pages/account/BomImportPage'));
const ProductReportPage = lazyWithRetry(() => import('@/pages/account/ProductReportPage'));
const LaborCostPage = lazyWithRetry(() => import('@/pages/account/LaborCostPage'));
const EnergyCostPage = lazyWithRetry(() => import('@/pages/account/EnergyCostPage'));
const DailyOutputPage = lazyWithRetry(() => import('@/pages/account/DailyOutputPage'));
const TechnicianManagePage = lazyWithRetry(() => import('@/pages/account/TechnicianManagePage'));
const SalaryManagePage = lazyWithRetry(() => import('@/pages/account/SalaryManagePage'));
const PieceworkWagePage = lazyWithRetry(() => import('@/pages/account/PieceworkWagePage'));
const PerformanceManagePage = lazyWithRetry(() => import('@/pages/account/PerformanceManagePage'));
const BudgetPlanPage = lazyWithRetry(() => import('@/pages/account/BudgetPlanPage'));
const BudgetPlanExecutePage = lazyWithRetry(() => import('@/pages/account/BudgetPlanExecutePage'));
const ApprovalCenterPage = lazyWithRetry(() => import('@/pages/approval/ApprovalCenterPage'));
const RolePermissionPage = lazyWithRetry(() => import('@/pages/settings/RolePermissionPage'));
const ConsultantManagePage = lazyWithRetry(() => import('@/pages/settings/ConsultantManagePage'));
const SettingsMailHubPage = lazyWithRetry(() => import('@/pages/settings/SettingsMailHubPage'));
const SettingsPointsHubPage = lazyWithRetry(() => import('@/pages/settings/SettingsPointsHubPage'));
const UserManagePage = lazyWithRetry(() => import('@/pages/settings/UserManagePage'));
const AllUsersPage = lazyWithRetry(() => import('@/pages/settings/AllUsersPage'));
// 管理端查看任意用户的推广/下线/分润数据（免借账号登录，v2.2.87）
const AdminUserReferralView = lazyWithRetry(() => import('@/pages/settings/AdminUserReferralView'));
// 利润自测线索中心（平台管理员）v2.2.42
const ProfitQuizLeadsPage = lazyWithRetry(() => import('@/pages/settings/ProfitQuizLeadsPage'));
const EmailSettingsPage = lazyWithRetry(() => import('@/pages/settings/EmailSettingsPage'));
const MyPointsPage = lazyWithRetry(() => import('@/pages/settings/MyPointsPage'));
const PointsRulesPage = lazyWithRetry(() => import('@/pages/settings/PointsRulesPage'));
// v2.2.89 个人资料：头像/昵称/简介/城市自助维护
const ProfilePage = lazyWithRetry(() => import('@/pages/settings/ProfilePage'));
const ProblemReportManagePage = lazyWithRetry(() => import('@/pages/settings/ProblemReportManagePage'));
const AnnouncementManagePage = lazyWithRetry(() => import('@/pages/settings/AnnouncementManagePage'));
const SplashScreenManagePage = lazyWithRetry(() => import('@/pages/settings/SplashScreenManagePage'));
const EnterpriseManagePage = lazyWithRetry(() => import('@/pages/settings/EnterpriseManagePage'));
const TenantInvitationPage = lazyWithRetry(() => import('@/pages/settings/TenantInvitationPage'));
const ReferralAdminPage = lazyWithRetry(() => import('@/pages/settings/ReferralAdminPage'));
const VerifyEmailPage = lazyWithRetry(() => import('@/pages/VerifyEmailPage'));
const ForgotPasswordPage = lazyWithRetry(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithRetry(() => import('@/pages/ResetPasswordPage'));
const HelpPage = lazyWithRetry(() => import('@/pages/HelpPage'));
const HelpManagePage = lazyWithRetry(() => import('@/pages/settings/HelpManagePage'));
const LegalManagePage = lazyWithRetry(() => import('@/pages/settings/LegalManagePage'));
const PkModulePage = lazyWithRetry(() => import('@/pages/account/PkModulePage'));
const OpportunityPage = lazyWithRetry(() => import('@/pages/opportunity/OpportunityPage'));
const CustomerAnalysisPage = lazyWithRetry(() => import('@/pages/opportunity/CustomerAnalysisPage'));
// 活动管理（互动吧式，v2.2.59）：管理端办活动 + 参与端报名
const ActivityManagePage = lazyWithRetry(() => import('@/pages/opportunity/ActivityManagePage'));
const ActivityAnalysisPage = lazyWithRetry(() => import('@/pages/opportunity/ActivityAnalysisPage'));
const ActivitiesPage = lazyWithRetry(() => import('@/pages/opportunity/ActivitiesPage'));
// 活动参与端（报名/签到）：独立路由，使二维码深链 #/my-activities?act=ID 可直达（此前仅作 /settings/points 的 tab 节点，深链会 404）
const MyActivitiesPage = lazyWithRetry(() => import('@/pages/opportunity/MyActivitiesPage'));
// v2.2.76 主讲嘉宾视角：列出「我是主讲嘉宾」的活动，可查看报名进度/签到/审核
const SpeakerActivitiesPage = lazyWithRetry(() => import('@/pages/opportunity/SpeakerActivitiesPage'));
// 营销产品管理（v2.2.84 方案 C）：打通 产品 ↔ 海报 ↔ 活动详情 联动
const ProductManagePage = lazyWithRetry(() => import('@/pages/opportunity/ProductManagePage'));
// 活动详情页（方案 A 付款前滚动展示介绍/亮点/议程/退款/往期；二维码深链 /activity/:id 入口）
const ActivityDetailPage = lazyWithRetry(() => import('@/pages/opportunity/ActivityDetailPage'));
const BehaviorAnalyticsPage = lazyWithRetry(() => import('@/pages/analytics/BehaviorAnalyticsPage'));
const TermsOfService = lazyWithRetry(() => import('@/pages/TermsOfService'));
const PrivacyPolicy = lazyWithRetry(() => import('@/pages/PrivacyPolicy'));
const LiveRegisterPage = lazyWithRetry(() => import('@/pages/LiveRegisterPage'));
const QuotationWorkbenchPage = lazyWithRetry(() => import('@/pages/account/QuotationWorkbenchPage'));
const MaterialPriceAdminPage = lazyWithRetry(() => import('@/pages/account/MaterialPriceAdminPage'));
const FlowCenterPage = lazyWithRetry(() => import('@/pages/account/FlowCenterPage'));
const QuotationRoleConfigPage = lazyWithRetry(() => import('@/pages/account/QuotationRoleConfigPage'));
const QuotationBoardPage = lazyWithRetry(() => import('@/pages/account/QuotationBoardPage'));
const NotificationCenterPage = lazyWithRetry(() => import('@/pages/NotificationCenterPage'));
// 我的工作台（v2.2.52）：首页聚合卡片
const WorkbenchPage = lazyWithRetry(() => import('@/pages/WorkbenchPage'));
// 审核中心（v2.2.53）：聚合所有平台级待审队列
const ReviewCenterPage = lazyWithRetry(() => import('@/pages/ReviewCenterPage'));
// 学习与成长（商学院）v2.0.41
const StudyCenterPage = lazyWithRetry(() => import('@/pages/learning/StudyCenterPage'));
const EbookStorePage = lazyWithRetry(() => import('@/pages/learning/EbookStorePage'));
const EbookDetailPage = lazyWithRetry(() => import('@/pages/learning/EbookDetailPage'));
const AuthorEbooksPage = lazyWithRetry(() => import('@/pages/learning/AuthorEbooksPage'));
const EbookOrdersPage = lazyWithRetry(() => import('@/pages/learning/EbookOrdersPage'));
const AuthorEbookFulfillmentPage = lazyWithRetry(() => import('@/pages/learning/AuthorEbookFulfillmentPage'));
// 平台管理：专著改价审计日志（同版本追加）v2.2.51
const EbookChangeLogPage = lazyWithRetry(() => import('@/pages/learning/EbookChangeLogPage'));
const CourseManagePage = lazyWithRetry(() => import('@/pages/learning/CourseManagePage'));
const ExamEditorPage = lazyWithRetry(() => import('@/pages/learning/ExamEditorPage'));
const ReviewsStatsPage = lazyWithRetry(() => import('@/pages/learning/ReviewsStatsPage'));
// 咨询师认证（KYC）v2.0.42
const ConsultantCertificationPage = lazyWithRetry(() => import('@/pages/consultant/ConsultantCertificationPage'));
// 我的推广（推荐返佣）v2.0.44
const MyPromotionPage = lazyWithRetry(() => import('@/pages/referral/MyPromotionPage'));
// 利润自测（获客转化）v2.0.48 —— 公开可答，完整诊断需注册
const ProfitQuizPage = lazyWithRetry(() => import('@/pages/ProfitQuizPage'));
// 登录后闪屏（启动页）v2.0.49
const SplashScreenPage = lazyWithRetry(() => import('@/pages/SplashScreenPage'));
// 咨询项目管理（CPM）v2.2.0：驾驶舱 / 列表 / 详情 / 我的工时
const ProjectDashboardPage = lazyWithRetry(() => import('@/pages/consulting/ProjectDashboardPage'));
const ProjectListPage = lazyWithRetry(() => import('@/pages/consulting/ProjectListPage'));
const ProjectDetailPage = lazyWithRetry(() => import('@/pages/consulting/ProjectDetailPage'));
const MyTimesheetsPage = lazyWithRetry(() => import('@/pages/consulting/MyTimesheetsPage'));
const PermissionTemplatePage = lazyWithRetry(() => import('@/pages/consulting/PermissionTemplatePage'));
// 客户只读门户（免登录，独立布局，不套 MainLayout）
const ProjectPortalPage = lazyWithRetry(() => import('@/pages/consulting/ProjectPortalPage'));
// 咨询师知识分享库（Knowledge Sharing Library，v2.2.7）：挂在 CPM 菜单下，路由保持 /knowledge
const KnowledgePage = lazyWithRetry(() => import('@/pages/knowledge/KnowledgePage'));
const KnowledgeUploadPage = lazyWithRetry(() => import('@/pages/knowledge/KnowledgeUploadPage'));
const MyKnowledgePage = lazyWithRetry(() => import('@/pages/knowledge/MyKnowledgePage'));
// 知识词条详情 / 在线编辑 / 版本历史（v2.2.21）
const KnowledgeArticlePage = lazyWithRetry(() => import('@/pages/knowledge/KnowledgeArticlePage'));
// 结构化 FMEA 风险库（v2.2.21，C 方案）
const FmeaLibraryPage = lazyWithRetry(() => import('@/pages/fmea/FmeaLibraryPage'));
// 问卷引擎（v2.2.32）：模板库 / 设计器 / 调查管理 / 结果看板
const SurveyTemplatesPage = lazyWithRetry(() => import('@/pages/survey/SurveyTemplatesPage'));
const SurveyDesignerPage = lazyWithRetry(() => import('@/pages/survey/SurveyDesignerPage'));
const SurveysPage = lazyWithRetry(() => import('@/pages/survey/SurveysPage'));
const SurveyResultPage = lazyWithRetry(() => import('@/pages/survey/SurveyResultPage'));
// 匿名填答页（免登录，不套 MainLayout）
const SurveyFillPage = lazyWithRetry(() => import('@/pages/survey/SurveyFillPage'));
// 会员运营 · 抽奖激励模块（v2.2.74）：抽奖中心 / 我的奖励 / 抽奖管理
const LotteryPage = lazyWithRetry(() => import('@/pages/lottery/LotteryPage'));
const LotteryRewardsPage = lazyWithRetry(() => import('@/pages/lottery/LotteryRewardsPage'));
const LotteryAdminPage = lazyWithRetry(() => import('@/pages/lottery/LotteryAdminPage'));

/** 路由守卫：需登录 + 已选租户 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token, currentTenant } = useAuthStore();
  const location = useLocation();
  if (!token) {
    const incoming = new URLSearchParams(location.search);
    // 方案 A：微信内、未登录、命中活动报名深链（或在活动页/活动详情页）→ 静默授权后回当前页，免去注册摩擦。
    if (isWechatBrowser() && (incoming.has('act') || location.pathname === '/my-activities' || location.pathname.startsWith('/activity'))) {
      // 静默授权回跳携带一次性 code（正等 App 顶层 effect 兑换 JWT）→ 悬挂，绝不跳 /login，避免打断兑换造成死循环
      if (incoming.has('code')) return null;
      // 兑换失败回落：带 wx_login_error 时不重试，交给下方 /login 兜底（带上错误便于排查）
      if (!incoming.has('wx_login_error')) {
        window.location.href = '/api/auth/wx/entry?back=' + encodeURIComponent(window.location.href);
        return null;
      }
    }
    // 微信扫码绑定回调先落受保护页（/settings/points?wxbind=..&msg=..），手机 webview 里
    // 没有登录态会被拦到 /login —— 结果参数必须透传，否则绑定成功/失败用户完全看不到
    //（2026-09-27 实测：只看到登录注册页，误以为"还是跳注册页"）。hash 路由下参数在 hash 内，须读 useLocation。
    const carry = new URLSearchParams();
    if (incoming.has('wxbind')) {
      carry.set('wxbind', incoming.get('wxbind') || '');
      carry.set('msg', incoming.get('msg') || '');
    }
    if (incoming.has('wx_login_error')) {
      // 静默登录失败原因必须透传到 /login 展示，否则错误被吞、排查全靠猜（2026-10-07 教训）
      carry.set('wx_login_error', incoming.get('wx_login_error') || '');
    }
    const qs = carry.toString();
    return <Navigate to={"/login" + (qs ? "?" + qs : "")} replace />;
  }
  if (!currentTenant) return <Navigate to="/tenant-select" replace />;
  return <>{children}</>;
};

const App: React.FC = () => {
  const location = useLocation();

  // 应用启动即捕获 URL 中的 ?invite，避免未登录访客被重定向到 /login 时丢失分享人标识
  useEffect(() => {
    captureInviteFromUrl();
  }, []);

  // 方案 A：微信内静默登录回跳携带一次性 ?code → 兑换 JWT/tenant/user 并落库，完成免注册登录。
  // 成功后清洗 hash（去掉已消费的 code，保留 act 深链）并整页 reload —— token 已持久化，
  // reload 后 ProtectedRoute 直接放行，绝不循环；失败则回落 /login 并带错误便于排查。
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // 取最后一个 code：401 兜底再入授权时，URL 可能同时残留旧 code（已消费），最后一个才是本次有效兑换码
    const codes = params.getAll('code');
    const code = codes.length ? codes[codes.length - 1] : '';
    if (!code) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiClient.get('/auth/wx/token?code=' + encodeURIComponent(code));
        const body = resp.data || {};
        const d = body.data;
        if (!d || body.code !== 0) throw new Error(body.message || '登录兑换失败');
        const store = useAuthStore.getState();
        store.setToken(d.token);
        if (d.tenant) store.setCurrentTenant({ id: d.tenant.id, name: d.tenant.name } as any, d.role as any);
        store.setUser(d.user as any);
        const sp = new URLSearchParams(location.search);
        sp.delete('code');
        // BrowserRouter：清洗 URL 用真实路径（此前 '#'+pathname 是 HashRouter 时代的写法，
        // 会把地址改写成带 # 的怪 URL 且参数路由层不可见 —— 2026-10-08 实测）
        const newUrl = location.pathname + (sp.toString() ? '?' + sp.toString() : '');
        if (!cancelled) window.location.replace(newUrl);
      } catch (e: any) {
        const sp = new URLSearchParams(location.search);
        sp.delete('code');
        sp.set('wx_login_error', (e && e.message) || '登录兑换失败，请重试');
        const newUrl = location.pathname + '?' + sp.toString();
        if (!cancelled) window.location.replace(newUrl);
      }
    })();
    return () => { cancelled = true; };
  }, [location.search]);

  // 页面行为埋点：路由切换上报 enter/leave，定时 ping 续命（详见 usePageTracking）
  usePageTracking();

  return (
    <Routes>
      {/* 认证相关 */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/tenant-select" element={<TenantSelect />} />
        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/live/register" element={<LiveRegisterPage />} />
        <Route path="/profit-quiz" element={<ProfitQuizPage />} />
      </Route>

      {/* 登录后闪屏（启动页）：需登录 + 已选租户 */}
      <Route
        element={
          <ProtectedRoute>
            <SplashScreenPage />
          </ProtectedRoute>
        }
        path="/splash"
      />

      {/* 客户只读门户（免登录）：客户凭 token 查看项目进度与交付物，不套 MainLayout */}
      <Route path="/portal/:token" element={<ProjectPortalPage />} />

      {/* 问卷匿名填答（免登录，v2.2.32）：客户员工扫码即填，不套 MainLayout */}
      <Route path="/s/:token" element={<SurveyFillPage />} />

      {/* 主业务区域 */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/workbench" element={<WorkbenchPage />} />
        <Route path="/review-center" element={<ReviewCenterPage />} />
        <Route path="/" element={<Dashboard />} />
        <Route path="/amoeba/structure" element={<StructurePage />} />
        <Route path="/amoeba/department" element={<DepartmentPage />} />
        <Route path="/amoeba/position" element={<PositionPage />} />
        <Route path="/amoeba/member" element={<MemberPage />} />
        <Route path="/amoeba/personnel-config" element={<PersonnelConfigPage />} />
        <Route path="/amoeba/org-chart" element={<OrgChartPage />} />
        <Route path="/account/define" element={<AccountDefinePage />} />
        <Route path="/account/config" element={<AccountConfigPage />} />
        <Route path="/entry/daily" element={<DailyEntryPage />} />
        <Route path="/sales/transactions" element={<SalesTransactionPage />} />
        <Route path="/report/create" element={<ReportCreatePage />} />
        <Route path="/report/monthly" element={<MonthlyReportPage />} />
        <Route path="/report/company" element={<CompanyRollupPage />} />
        <Route path="/report/annual" element={<AnnualReportPage />} />
        <Route path="/account/transactions" element={<TransactionListPage />} />
        <Route path="/account/transaction-initiate" element={<TransactionInitiatePage />} />
        <Route path="/account/templates-market" element={<TemplateMarketPage />} />
        <Route path="/account/import" element={<DataImportPage />} />
        <Route path="/account/allocation" element={<AllocationExecutePage />} />
        <Route path="/account/bom-import" element={<BomImportPage />} />
        <Route path="/account/product-report" element={<ProductReportPage />} />
        <Route path="/account/labor-cost" element={<LaborCostPage />} />
        <Route path="/account/energy-cost" element={<EnergyCostPage />} />
        <Route path="/account/quotation" element={<QuotationWorkbenchPage defaultTab="quotation" />} />
        <Route path="/account/quotation-market" element={<QuotationMarketPage />} />
        <Route path="/account/my-quotations" element={<QuotationWorkbenchPage defaultTab="my-quotations" />} />
        <Route path="/account/customer-demands" element={<QuotationWorkbenchPage defaultTab="customer-demands" />} />
        <Route path="/account/quotation-flow" element={<FlowCenterPage />} />
        <Route path="/account/material-prices" element={<MaterialPriceAdminPage />} />
        <Route path="/account/quotation-roles" element={<QuotationRoleConfigPage />} />
        <Route path="/account/quotation-board" element={<QuotationBoardPage />} />
        <Route path="/notifications" element={<NotificationCenterPage />} />
        <Route path="/account/daily-output" element={<DailyOutputPage />} />
        <Route path="/account/piecework-wage" element={<PieceworkWagePage />} />
        <Route path="/account/technician" element={<TechnicianManagePage />} />
        <Route path="/account/performance" element={<PerformanceManagePage />} />
        <Route path="/account/salary" element={<SalaryManagePage />} />
        <Route path="/account/pk" element={<PkModulePage />} />
        <Route path="/target/goal" element={<BudgetPlanPage />} />
        <Route path="/target/execution" element={<BudgetPlanExecutePage />} />
        <Route path="/target" element={<Navigate to="/target/goal" replace />} />
        <Route path="/approval/pending" element={<ApprovalCenterPage />} />
        <Route path="/approval/process-owner" element={<ApprovalCenterPage />} />
        <Route path="/approval" element={<Navigate to="/approval/pending" replace />} />
        <Route path="/approval/process" element={<ApprovalCenterPage />} />
        <Route path="/approval/stats" element={<ApprovalCenterPage />} />
        <Route path="/settings/roles" element={<RolePermissionPage />} />
        <Route path="/settings/consultants" element={<ConsultantManagePage />} />
        <Route path="/opportunity" element={<OpportunityPage />} />
        <Route path="/customer-analysis" element={<CustomerAnalysisPage />} />
        <Route path="/activity-manage" element={<ActivityManagePage />} />
        <Route path="/activity-analysis" element={<ActivityAnalysisPage />} />
        {/* v2.2.65 活动列表页（独立入口，网格卡片，二维码/分享深链可分流至此） */}
        <Route path="/activities" element={<ActivitiesPage />} />
        {/* 活动参与端（报名/签到）：独立路由，使二维码深链 #/my-activities?act=ID 可直达（方案 A 免注册报名入口） */}
        <Route path="/my-activities" element={<MyActivitiesPage />} />
        {/* v2.2.76 主讲嘉宾视角：我是主讲嘉宾的活动（报名进度/签到/审核/提醒） */}
        <Route path="/speaker-activities" element={<SpeakerActivitiesPage />} />
        {/* v2.2.84 方案 C：营销产品管理（产品 ↔ 海报 ↔ 活动详情 联动） */}
        <Route path="/product-manage" element={<ProductManagePage />} />
        {/* 活动详情页（方案 A：付款前滚动展示介绍/亮点/议程/退款/往期），二维码深链 /activity/:id 入口 */}
        <Route path="/activity/:id" element={<ActivityDetailPage />} />
        <Route path="/analytics" element={<BehaviorAnalyticsPage />} />
        <Route path="/settings/users" element={<UserManagePage />} />
        <Route path="/settings/mail" element={<SettingsMailHubPage />} />
        <Route path="/settings/points" element={<SettingsPointsHubPage />} />
        {/* v2.2.89 个人资料：头像/昵称/简介/城市自助维护 */}
        <Route path="/profile" element={<ProfilePage />} />
        {/* 旧路由保留做向后兼容（菜单已折叠进 Hub） */}
        <Route path="/settings/all-users" element={<AllUsersPage />} />
        {/* 管理端：查看任意用户的推广数据（上线/下线/分润） v2.2.87 */}
        <Route path="/admin/user-referral/:userId" element={<AdminUserReferralView />} />
        <Route path="/settings/profit-quiz-leads" element={<ProfitQuizLeadsPage />} />
        <Route path="/settings/email" element={<EmailSettingsPage />} />
        <Route path="/settings/my-points" element={<MyPointsPage />} />
        <Route path="/settings/points-rules" element={<PointsRulesPage />} />
        <Route path="/settings/enterprise" element={<EnterpriseManagePage />} />
        <Route path="/settings/tenant-invitation" element={<TenantInvitationPage />} />
        <Route path="/settings/referral" element={<ReferralAdminPage />} />
        <Route path="/settings/help" element={<HelpManagePage />} />
        <Route path="/settings/legal" element={<LegalManagePage />} />
        <Route path="/settings/problem-reports" element={<ProblemReportManagePage />} />
        <Route path="/settings/announcements" element={<AnnouncementManagePage />} />
        <Route path="/settings/splash-screens" element={<SplashScreenManagePage />} />
        <Route path="/settings" element={<Navigate to="/settings/roles" replace />} />
        {/* 学习与成长（商学院） */}
        <Route path="/learning" element={<Navigate to="/learning/study" replace />} />
        <Route path="/learning/study" element={<StudyCenterPage />} />
        <Route path="/learning/courses" element={<CourseManagePage />} />
        <Route path="/learning/exams" element={<ExamEditorPage />} />
        <Route path="/learning/reviews" element={<ReviewsStatsPage />} />
        {/* 专著（电子书）v2.2.43：积分购买 + 作者分成 */}
        <Route path="/learning/ebooks" element={<EbookStorePage />} />
        <Route path="/learning/ebooks/:id" element={<EbookDetailPage />} />
        <Route path="/learning/author-ebooks" element={<AuthorEbooksPage />} />
        <Route path="/learning/ebook-orders" element={<EbookOrdersPage />} />
        <Route path="/learning/ebook-fulfillment" element={<AuthorEbookFulfillmentPage />} />
        <Route path="/learning/admin/ebook-changes" element={<EbookChangeLogPage />} />
        {/* 咨询项目管理（CPM）v2.2.0 */}
        <Route path="/consulting" element={<Navigate to="/consulting/dashboard" replace />} />
        <Route path="/consulting/dashboard" element={<ProjectDashboardPage />} />
        <Route path="/consulting/projects" element={<ProjectListPage />} />
        <Route path="/consulting/projects/:id" element={<ProjectDetailPage />} />
        <Route path="/consulting/timesheets" element={<MyTimesheetsPage />} />
        <Route path="/consulting/permission-template" element={<PermissionTemplatePage />} />
        {/* 咨询师知识分享库（Knowledge Sharing Library，v2.2.7）：CPM 子模块 */}
        <Route path="/knowledge" element={<KnowledgePage />} />
        <Route path="/knowledge/upload" element={<KnowledgeUploadPage />} />
        <Route path="/knowledge/my" element={<MyKnowledgePage />} />
        {/* 知识词条：在线阅读 + 协同编辑 + 版本回滚（v2.2.21） */}
        <Route path="/knowledge/article/:id" element={<KnowledgeArticlePage />} />
        <Route path="/fmea/library" element={<FmeaLibraryPage />} />
        {/* 问卷引擎（v2.2.32）：模板 → 发起调查 → 匿名回收 → 逐题统计 */}
        <Route path="/surveys" element={<SurveysPage />} />
        <Route path="/surveys/templates" element={<SurveyTemplatesPage />} />
        <Route path="/surveys/designer" element={<SurveyDesignerPage />} />
        <Route path="/surveys/designer/:id" element={<SurveyDesignerPage />} />
        <Route path="/surveys/:id/result" element={<SurveyResultPage />} />
        <Route path="/consultant/certification" element={<ConsultantCertificationPage />} />
        <Route path="/referral" element={<MyPromotionPage />} />
        <Route path="/help" element={<HelpPage />} />
        {/* 会员运营 · 抽奖激励模块（v2.2.74） */}
        <Route path="/lottery" element={<LotteryPage />} />
        <Route path="/lottery/rewards" element={<LotteryRewardsPage />} />
        <Route path="/lottery/admin" element={<LotteryAdminPage />} />
      </Route>

      {/* 404 回退 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

/**
 * 页面行为埋点：监听路由变化，进入页面发 enter、离开发 leave、每 30s ping 续命。
 * - 仅在已登录（有 token）时上报，避免未登录的匿名噪音。
 * - 走 apiClient（自动带 Authorization），后端 header 通道验签失败降级 decode，身份稳定。
 * - 页面直接卸载（不触发路由 cleanup）时 beforeunload + sendBeacon 兜底补 leave（token 放 body）。
 */
function usePageTracking() {
  const location = useLocation();
  const prevRef = useRef<{ path: string; t: number } | null>(null);

  useEffect(() => {
    const token = useAuthStore.getState().token;
    if (!token) {
      prevRef.current = null;
      return;
    }
    const sid = getOrCreateSessionId();
    const path = location.pathname;
    analyticsApi.track({ type: 'enter', page_path: path, session_id: sid });
    prevRef.current = { path, t: Date.now() };
    const iv = setInterval(() => {
      analyticsApi.track({ type: 'ping', page_path: path, session_id: sid });
    }, 30000);
    return () => {
      clearInterval(iv);
      if (prevRef.current) {
        analyticsApi.track({
          type: 'leave',
          page_path: path,
          session_id: sid,
          duration_ms: Date.now() - prevRef.current.t,
        });
      }
    };
  }, [location.pathname]);

  // 页面直接关闭（卸载不触发路由 cleanup）兜底补 leave
  useEffect(() => {
    const sid = getOrCreateSessionId();
    const onUnload = () => {
      const token = useAuthStore.getState().token;
      const cur = prevRef.current;
      if (!cur) return;
      try {
        const payload = {
          type: 'leave',
          page_path: cur.path,
          session_id: sid,
          duration_ms: Date.now() - cur.t,
          token: token || undefined,
        };
        navigator.sendBeacon('/api/analytics/track', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
      } catch {
        /* 兜底失败不影响主流程 */
      }
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);
}

/** 每浏览器会话一个稳定 sessionId（sessionStorage） */
function getOrCreateSessionId(): string {
  try {
    let sid = sessionStorage.getItem('ib_pv_sid');
    if (!sid) {
      sid = 'pv_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
      sessionStorage.setItem('ib_pv_sid', sid);
    }
    return sid;
  } catch {
    return 'pv_' + Date.now().toString(36);
  }
}

export default App;
